import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus, Building2, Phone, Mail, Globe, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from '@/components/ui/use-toast';
import { bankingPartnerService } from '../services/bankingPartnerService';
import { BankAgreement, BankContact } from '../types';
import { PricingTemplatesList } from './PricingTemplatesList';
import { PricingTemplateForm } from './PricingTemplateForm';
import type { Timestamp } from 'firebase/firestore';

const isFirestoreTimestamp = (value: unknown): value is Timestamp => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate?: unknown }).toDate === 'function'
  );
};

const getStatusColor = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case 'active':
      return "default";
    case 'inactive':
      return "destructive";
    case 'pending':
      return "secondary";
    default:
      return "outline";
  }
};

const formatDate = (timestamp: Timestamp | Date | string | null | undefined): string => {
  if (!timestamp) return 'N/A';
  const date = isFirestoreTimestamp(timestamp)
    ? timestamp.toDate()
    : timestamp instanceof Date
      ? timestamp
      : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export function BankingPartnerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [deleteAgreementId, setDeleteAgreementId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | undefined>();

  const { data: partner, isLoading: isLoadingPartner } = useQuery({
    queryKey: ['bankingPartner', id],
    queryFn: () => bankingPartnerService.getBankingPartner(id!),
    enabled: !!id,
  });

  const { data: contacts = [], isLoading: isLoadingContacts } = useQuery({
    queryKey: ['bankingPartnerContacts', id],
    queryFn: () => bankingPartnerService.getContactsByPartnerId(id!),
    enabled: !!id,
  });

  const { data: agreements = [], isLoading: isLoadingAgreements } = useQuery({
    queryKey: ['bankingPartnerAgreements', id],
    queryFn: () => bankingPartnerService.getAgreementsByPartnerId(id!),
    enabled: !!id,
  });

  const mainContact = contacts.find((contact: BankContact) => contact.isMainContact);
  const activeAgreement = agreements.find((agreement: BankAgreement) => agreement.status === 'active');

  const handleColorChange = async (color: string) => {
    try {
      await bankingPartnerService.updateBankingPartner(id!, { color });
      await queryClient.invalidateQueries({ queryKey: ['bankingPartner', id] });
      toast({
        title: 'Success',
        description: 'Partner color updated successfully',
      });
    } catch (error) {
      console.error('Error updating partner color:', error);
      toast({
        title: 'Error',
        description: 'Failed to update partner color',
        variant: 'destructive',
      });
    }
  };

  const handleEditAgreement = (agreementId: string) => {
    navigate(`/app/banking-partners/${id}/agreements/${agreementId}/edit`);
  };

  const handleDeleteAgreement = async (agreementId: string) => {
    try {
      await bankingPartnerService.deleteAgreement(agreementId);
      await queryClient.invalidateQueries({ queryKey: ['bankingPartnerAgreements', id] });
      toast({
        title: 'Success',
        description: 'Agreement deleted successfully',
      });
      setDeleteAgreementId(null);
    } catch (error) {
      console.error('Error deleting agreement:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete agreement',
        variant: 'destructive',
      });
    }
  };

  if (isLoadingPartner) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Banking partner not found</p>
        <Button className="mt-4" onClick={() => navigate('/app/banking-partners')}>
          Back to Partners
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/app/banking-partners')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{partner.name}</h1>
            <p className="text-muted-foreground">
              Banking Partner Details
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={getStatusColor(partner.status)}>
            {partner.status.charAt(0).toUpperCase() + partner.status.slice(1)}
          </Badge>
          <Button
            variant="outline"
            onClick={() => navigate(`/app/banking-partners/${id}/edit`)}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit Partner
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="agreements">Agreements</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
          <TabsTrigger value="pricingTemplates">Pricing Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Partner Name</Label>
                  <p className="font-medium">{partner.name}</p>
                </div>
                {partner.description && (
                  <div>
                    <Label className="text-muted-foreground">Description</Label>
                    <p className="font-medium">{partner.description}</p>
                  </div>
                )}
                {partner.website && (
                  <div>
                    <Label className="text-muted-foreground">Website</Label>
                    <a
                      href={partner.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                    >
                      {partner.website}
                      <Globe className="h-3 w-3" />
                    </a>
                  </div>
                )}
                <div>
                  <Label className="text-muted-foreground">Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="color"
                      value={partner.color || '#000000'}
                      onChange={(e) => handleColorChange(e.target.value)}
                      className="w-16 h-10"
                    />
                    <span className="font-medium">{partner.color || '#000000'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Main Contact</CardTitle>
              </CardHeader>
              <CardContent>
                {mainContact ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <p className="font-medium">{mainContact.name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <a href={`mailto:${mainContact.email}`} className="text-blue-600 hover:underline">
                        {mainContact.email}
                      </a>
                    </div>
                    {mainContact.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <a href={`tel:${mainContact.phone}`} className="text-blue-600 hover:underline">
                          {mainContact.phone}
                        </a>
                      </div>
                    )}
                    <div className="text-sm text-muted-foreground">
                      {mainContact.role} • {mainContact.department}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No main contact assigned</p>
                    <Button
                      className="mt-4"
                      size="sm"
                      onClick={() => setActiveTab('contacts')}
                    >
                      Add Contact
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {activeAgreement && (
            <Card>
              <CardHeader>
                <CardTitle>Active Agreement</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <h4 className="font-medium mb-3">Low Risk Terms</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Processing (Visa/MC/Disc)</span>
                        <span>{activeAgreement.lowRisk.processingFees.visaMasterDiscover}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Processing (Amex)</span>
                        <span>{activeAgreement.lowRisk.processingFees.amex}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Transaction Fee</span>
                        <span>${activeAgreement.lowRisk.transactionFees.visaMasterDiscover}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Monthly Fee</span>
                        <span>${activeAgreement.lowRisk.monthlyFee}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-3">High Risk Terms</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Processing (Visa/MC/Disc)</span>
                        <span>{activeAgreement.highRisk.processingFees.visaMasterDiscover}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Processing (Amex)</span>
                        <span>{activeAgreement.highRisk.processingFees.amex}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Transaction Fee</span>
                        <span>${activeAgreement.highRisk.transactionFees.visaMasterDiscover}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Monthly Fee</span>
                        <span>${activeAgreement.highRisk.monthlyFee}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="contacts" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Contacts</h3>
            <Button
              size="sm"
              onClick={() => navigate(`/app/banking-partners/${id}/contacts/new`)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Contact
            </Button>
          </div>
          {isLoadingContacts ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : contacts.length > 0 ? (
            <div className="grid gap-4">
              {contacts.map((contact) => (
                <Card key={contact.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="font-medium">{contact.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {contact.role} • {contact.department}
                        </p>
                        <div className="flex items-center gap-4 text-sm">
                          <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">
                            {contact.email}
                          </a>
                          {contact.phone && (
                            <a href={`tel:${contact.phone}`} className="text-blue-600 hover:underline">
                              {contact.phone}
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {contact.isMainContact && (
                          <Badge variant="outline">Main Contact</Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/app/banking-partners/${id}/contacts/${contact.id}/edit`)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <p>No contacts added yet</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="agreements" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Agreements</h3>
            <Button
              size="sm"
              onClick={() => navigate(`/app/banking-partners/${id}/agreements/new`)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Agreement
            </Button>
          </div>
          {isLoadingAgreements ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : agreements.length > 0 ? (
            <div className="grid gap-4">
              {agreements.map((agreement) => (
                <Card key={agreement.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant={agreement.status === 'active' ? 'default' : 'secondary'}>
                            {agreement.status.charAt(0).toUpperCase() + agreement.status.slice(1)}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(agreement.startDate)} - {agreement.endDate ? formatDate(agreement.endDate) : 'Ongoing'}
                          </span>
                        </div>
                        <div className="grid gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Low Risk: </span>
                            {agreement.lowRisk.processingFees.visaMasterDiscover}% + ${agreement.lowRisk.transactionFees.visaMasterDiscover}
                          </div>
                          <div>
                            <span className="text-muted-foreground">High Risk: </span>
                            {agreement.highRisk.processingFees.visaMasterDiscover}% + ${agreement.highRisk.transactionFees.visaMasterDiscover}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditAgreement(agreement.id)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteAgreementId(agreement.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <p>No agreements added yet</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="notes">
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <p>Notes feature coming soon</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pricingTemplates" className="space-y-4">
          {showTemplateForm ? (
            <PricingTemplateForm
              partnerId={id!}
              templateId={editingTemplateId}
              onBack={() => {
                setShowTemplateForm(false);
                setEditingTemplateId(undefined);
              }}
              onSaved={() => {
                setShowTemplateForm(false);
                setEditingTemplateId(undefined);
              }}
            />
          ) : (
            <PricingTemplatesList
              partnerId={id!}
              onCreateTemplate={() => {
                setEditingTemplateId(undefined);
                setShowTemplateForm(true);
              }}
              onEditTemplate={(templateId) => {
                setEditingTemplateId(templateId);
                setShowTemplateForm(true);
              }}
            />
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!deleteAgreementId} onOpenChange={() => setDeleteAgreementId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the agreement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteAgreementId && handleDeleteAgreement(deleteAgreementId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
